import { computed } from 'vue';
import type { Component } from 'vue';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import {
  Building2,
  ClipboardCheck,
  Factory,
  GitBranch,
  Home,
  ShoppingBasket,
  ShoppingCart,
  Route,
  Users,
  Warehouse
} from 'lucide-vue-next';

import type { ThemeContext } from '@shared/composables/useTheme';
import type { useAuthStore } from '@app/stores/auth';
import { useAttendanceLockStore } from '@features/attendance/attendanceLockStore';
import { useUserRoleRouteGroups } from '@shared/reference-catalogs/useUserRoleCatalog';

type NavLink = { to: string; label: string; icon: Component };

export function useNavigation(
  auth: ReturnType<typeof useAuthStore>,
  theme: ThemeContext,
  route: RouteLocationNormalizedLoaded
) {
  const attendanceLock = useAttendanceLockStore();
  void theme;
  const navigationLocked = computed(() => attendanceLock.isLocked);
  const { hasRouteGroup } = useUserRoleRouteGroups({
    authHeaders: () => auth.authHeaders,
    roles: () => auth.user?.roles ?? []
  });

  const navLinks = computed<NavLink[]>(() => {
    const links: NavLink[] = [];
    if (hasRouteGroup('injection-molding-assignment')) links.push({ to: '/workplaces/injection-molding-assignments', label: 'Станки литья', icon: Factory });
    if (navigationLocked.value) {
      return links;
    }

    const canUsePlanning = hasRouteGroup('planning-workspace');
    const hasCounterpartyAccess = hasRouteGroup('counterparty-workspace');
    const hasInventoryAccess = hasRouteGroup('inventory-workspace');
    const hasProductionRouteAccess = hasRouteGroup('production-route-launch-create');

    if (auth.isAuthenticated) {
      links.push({ to: '/dashboard', label: 'Главная', icon: Home });
    }

    if (hasCounterpartyAccess) {
      links.push({
        to: '/sales-orders',
        label: 'Заказы клиентов',
        icon: ShoppingCart
      });
    }

    if (canUsePlanning) {
      links.push({ to: '/planning', label: 'Планирование', icon: ClipboardCheck });
    }

    if (hasProductionRouteAccess) {
      links.push({ to: '/production/route-launches', label: 'Маршруты партий', icon: Route });
    }

    if (hasRouteGroup('production-control-workspace')) {
      links.push({ to: '/assignments', label: 'Контроль производства', icon: ClipboardCheck });
    }

    if (hasRouteGroup('manufacturing-workspace')) {
      links.push({ to: '/manufacturing', label: 'Производство', icon: Factory });
    }

    if (hasRouteGroup('process-builder')) {
      links.push({
        to: '/workflows-next',
        label: 'Конструктор процессов',
        icon: GitBranch
      });
    }

    if (hasRouteGroup('workplace-management')) {
      links.push({ to: '/workplaces', label: 'Рабочие места', icon: Factory });
      links.push({ to: '/workplaces/injection-molding-tooling', label: 'Пресс-формы', icon: Factory });
    }

    if (hasInventoryAccess) {
      links.push({ to: '/inventory', label: 'Склад', icon: Warehouse });
    }

    if (hasRouteGroup('procurement-workspace')) {
      links.push({ to: '/procurement', label: 'Снабжение', icon: ShoppingBasket });
    }

    if (hasCounterpartyAccess) {
      links.push({
        to: '/counterparties',
        label: 'Контрагенты',
        icon: Building2
      });
    }

    if (hasRouteGroup('user-management')) {
      links.push({ to: '/users', label: 'Пользователи', icon: Users });
    }

    return links;
  });

  const isPublicRoute = computed(() => route.meta?.public === true);
  const layoutVariant = computed<'standard' | 'workspace'>(() =>
    route.meta?.layoutVariant === 'workspace' ? 'workspace' : 'standard'
  );
  const showNavigation = computed(() => navLinks.value.length > 0 && !isPublicRoute.value);
  const activeNavPath = computed(() => {
    const matchingLinks = navLinks.value.filter(
      (link) => route.path === link.to || route.path.startsWith(`${link.to}/`)
    );

    return matchingLinks.sort((left, right) => right.to.length - left.to.length)[0]?.to ?? null;
  });
  const layoutClasses = computed(() => {
    if (!showNavigation.value) {
      return 'flex w-full flex-col items-center justify-center min-h-[calc(100vh-160px)]';
    }

    if (layoutVariant.value === 'workspace') {
      return 'grid w-full max-w-none gap-5 lg:grid-cols-[256px_minmax(0,1fr)] xl:gap-6';
    }

    return 'grid w-full max-w-none gap-5 lg:grid-cols-[256px_minmax(0,1fr)] xl:gap-[44px]';
  });
  const isNavLinkActive = (to: string) => activeNavPath.value === to;
  const showScannerShortcut = computed(
    () => !navigationLocked.value && hasRouteGroup('operator-workspace')
  );
  const attendanceShiftLockActive = computed(() =>
    auth.isAuthenticated && attendanceLock.required && attendanceLock.active
  );
  const showAttendanceEndShiftShortcut = computed(() =>
    attendanceShiftLockActive.value && !attendanceLock.exempt && !navigationLocked.value
  );

  return {
    navLinks,
    isPublicRoute,
    layoutVariant,
    showNavigation,
    layoutClasses,
    isNavLinkActive,
    showScannerShortcut,
    showAttendanceEndShiftShortcut,
    navigationLocked
  };
}
